import Image from 'next/image';
import React from 'react';
import { styled } from '@linaria/react';
import { useSpring, animated } from 'react-spring';

import useIntersectionRatio from '@/hooks/use-intersection-ratio';

const ROOT = '/images/pixel-perfection';
const MACHINE = `${ROOT}/dp-machine.png`;
const DESIGNER = `${ROOT}/dp-designer.png`;
const MYSTERY_BOX = `${ROOT}/dp-mystery-box.png`;
const LABEL_IMPLEMENTATION = `${ROOT}/dp-the-implementation-process.png`;
const LABEL_DESIGNER = `${ROOT}/dp-the-designer-label.png`;

/**
 * Step map:
 *
 * 0: Initial
 * 1: Show designer
 * 2: Show mystery box
 * 3: Show 3 labels
 */

const DesignerPerspectiveIllustration = () => {
  const [step, setStep] = React.useState(0);
  const [hoveringPackage, setHoveringPackage] =
    React.useState<boolean>(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const intersectionRatio = useIntersectionRatio(wrapperRef);

  React.useEffect(() => {
    if (intersectionRatio < 0.1) {
      // Reset everything!
      setStep(0);
    } else if (intersectionRatio > 0.55 && step < 1) {
      setStep(1);
    } else if (intersectionRatio > 0.72 && step < 2) {
      setStep(2);
    } else if (intersectionRatio > 0.875 && step < 3) {
      setStep(3);
    }
  }, [intersectionRatio, step]);

  const slowSpringConfig = {
    tension: 120,
    friction: 40,
  };

  const designerSpring = useSpring({
    transform: `translate(${step < 1 ? -15 : 0}%, ${
      step < 1 ? -5 : 0
    }%)`,
    opacity: step < 1 ? 0 : 1,
    config: step >= 1 ? slowSpringConfig : undefined,
  });

  const mysteryBoxContainerSpring = useSpring({
    transform: hoveringPackage
      ? `translate(0%, -12.5%) scale(1.1)`
      : `translate(0%, 0%) scale(1)`,
    config: {
      tension: 250,
      friction: 20,
    },
  });

  const mysteryBoxSpring = useSpring({
    transform: `translate(${step < 2 ? -30 : 0}%, ${
      step < 2 ? -10 : 0
    }%) scale(1)`,
    opacity: step < 2 ? 0 : 1,
    config: step >= 2 ? slowSpringConfig : undefined,
  });

  const mockupLabelSpring = useSpring({
    transform: `translate(${step < 1 ? -20 : 0}%, ${
      step < 1 ? -6 : 0
    }%)`,
    opacity: step < 1 ? 0 : 1,
    config:
      step >= 1
        ? {
            ...slowSpringConfig,
            friction: 75,
          }
        : undefined,
  });

  const implementationSpring = useSpring({
    transform: `translate(${step < 3 ? 20 : 0}%, ${
      step < 3 ? 5 : 0
    }%)`,
    opacity: step < 3 ? 0 : 1,
    config:
      step >= 3
        ? {
            ...slowSpringConfig,
            friction: 50,
          }
        : undefined,
  });

  return (
    <Wrapper ref={wrapperRef}>
      <ImplementationLabel
        alt=""
        src={LABEL_IMPLEMENTATION}
        style={implementationSpring}
      />
      <DesignerLabel
        alt=""
        src={LABEL_DESIGNER}
        style={mockupLabelSpring}
      />
      <MysteryBoxContainer
        onMouseEnter={() => setHoveringPackage(true)}
        onMouseLeave={() => setHoveringPackage(false)}
        style={mysteryBoxContainerSpring}
      >
        <MysteryBox
          alt=""
          src={MYSTERY_BOX}
          style={mysteryBoxSpring}
        />
      </MysteryBoxContainer>
      <Designer alt="" src={DESIGNER} style={designerSpring} />
      <Machine
        alt="A large machine, similar to an airport scanner. It has a conveyor belt. A designer places a gift at one end, and it feeds into the machine. At the other end, a box covered in question marks emerges."
        src={MACHINE}
        width={675}
        height={527}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  margin-bottom: 48px;

  img {
    display: block;
  }
`;

const Machine = styled(Image)`
  position: relative;
  z-index: 3;
`;

const Designer = styled(animated.img)`
  position: absolute;
  left: 0;
  top: 8%;
  height: ${(564 / 1054) * 100}%;
`;

const MysteryBoxContainer = styled(animated.div)`
  position: absolute;
  z-index: 4;
  top: 60%;
  right: 12%;
  width: ${(162 / 1350) * 100}%;
`;

const MysteryBox = styled(animated.img)`
  width: 100%;
  filter: drop-shadow(0px 6px 24px hsla(0deg, 0%, 100%, 0.75));
`;

const Label = styled(animated.img)`
  position: absolute;
`;

const DesignerLabel = styled(Label)`
  top: -2%;
  left: 5%;
  width: ${(497 / 1350) * 100}%;
`;
const ImplementationLabel = styled(Label)`
  bottom: 6%;
  left: 0%;
  width: ${(457 / 1350) * 100}%;
`;

export default DesignerPerspectiveIllustration;
